/**
 * Admin-only payment-status wizard.
 *
 * Review a guardian and their children's current payment status, applied
 * promo code(s), and projected next-year renewal amount, then correct any of
 * payment_status / subscription_type / auto_renew / referral_code directly.
 *
 * This is a DB-only override -- it never calls Stripe, never charges or
 * refunds anything. It just edits the state that Stripe events and the
 * real renewal handler read. The three-step flow (review -> edit -> confirm)
 * exists so an admin always sees exactly what is changing, for whom, before
 * anything is written.
 */
import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";
import { FormControl, MenuItem, Select, TextField } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import palette from "assets/theme/base/palette";
import { call } from "services/axiosService/core";
import * as Sentry from "@sentry/browser";

const PAYMENT_STATUS_CHIP = {
  success: { bg: "#e8f5e9", color: "#4caf50" },
  pending: { bg: "#fff3e0", color: "#ff9800" },
  fail: { bg: "#fdecea", color: "#f44336" },
};

const SUBSCRIPTION_TYPES = ["maxu_yearly", "true_mindsets_yearly", "true_mindsets_monthly"];
const PAYMENT_STATUSES = ["success", "pending", "fail"];

const PaymentStatusChip = ({ status }) => {
  const c = PAYMENT_STATUS_CHIP[status] || PAYMENT_STATUS_CHIP.pending;
  return (
    <Chip
      label={status || "unknown"}
      size="small"
      sx={{ bgcolor: c.bg, color: c.color, fontWeight: "bold", fontSize: "0.7rem", height: 22 }}
    />
  );
};

const money = (amount) => `$${Number(amount ?? 0).toFixed(0)}`;

const PaymentWizardDialog = ({
  openPaymentWizardDialog,
  ClosePaymentWizardDialogHandler,
  selectUserId,
  setOpenSnackbar,
  setAlertMessage,
}) => {
  const [step, setStep] = useState("review");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [overview, setOverview] = useState(null);
  const [editingIds, setEditingIds] = useState({});
  const [edits, setEdits] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    call(`users/${selectUserId}/payment-overview`, "GET", {})
      .then((res) => {
        setLoading(false);
        if (res.status == 200) {
          setOverview(res.data);
        } else {
          setLoadError(res?.data?.message || "Could not load payment details.");
        }
      })
      .catch((err) => {
        setLoading(false);
        Sentry.captureException(err);
        setLoadError("Could not load payment details.");
      });
  }, [selectUserId]);

  const toggleEditing = (personId) => {
    setEditingIds((prev) => ({ ...prev, [personId]: !prev[personId] }));
  };

  const setFieldEdit = (personId, field, value) => {
    setEdits((prev) => ({
      ...prev,
      [personId]: { ...prev[personId], [field]: value },
    }));
  };

  const changedEntries = Object.entries(edits)
    .map(([id, fields]) => ({ id, fields }))
    .filter(({ fields }) => fields && Object.keys(fields).length > 0);

  const personById = (id) => (overview?.people || []).find((p) => p.id === id);

  const submit = () => {
    setSubmitting(true);
    setSubmitError("");
    call(`users/${selectUserId}/payment-override`, "PUT", {
      body: {
        overrides: changedEntries.map(({ id, fields }) => ({ id, ...fields })),
      },
    })
      .then((res) => {
        setSubmitting(false);
        if (res.status == 200) {
          setStep("done");
          setOpenSnackbar(true);
          setAlertMessage("Payment status updated.");
        } else {
          setSubmitError(res?.data?.message || "Error while updating payment status.");
        }
      })
      .catch((err) => {
        setSubmitting(false);
        Sentry.captureException(err);
        setSubmitError("Error while updating payment status.");
      });
  };

  return (
    <Dialog
      open={openPaymentWizardDialog}
      onClose={ClosePaymentWizardDialogHandler}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>Manage Payments</DialogTitle>
      <DialogContent>
        {loading && (
          <MDBox display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </MDBox>
        )}

        {!loading && loadError && (
          <MDTypography variant="body2" color="error">
            {loadError}
          </MDTypography>
        )}

        {!loading && !loadError && overview && step === "review" && (
          <MDBox>
            <MDTypography variant="body2" color="text.secondary" mb={2}>
              Current payment status for this family. Nothing here has been changed yet.
            </MDTypography>
            {overview.people.map((person) => (
              <MDBox
                key={person.id}
                sx={{
                  border: `1px solid ${palette.neutral[200]}`,
                  borderRadius: "12px",
                  p: 2,
                  mb: 1.5,
                }}
              >
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <MDTypography variant="body1" fontWeight="bold">
                    {person.name || person.email}{" "}
                    {person.id === overview.guardian_id && (
                      <MDTypography component="span" variant="caption" color="text.secondary">
                        (guardian)
                      </MDTypography>
                    )}
                  </MDTypography>
                  <PaymentStatusChip status={person.payment_status} />
                </MDBox>
                <MDTypography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {person.email}
                </MDTypography>
                <MDBox display="flex" gap={3} mt={1} flexWrap="wrap">
                  <MDTypography variant="caption">
                    Subscription: <b>{person.subscription_type}</b>
                  </MDTypography>
                  <MDTypography variant="caption">
                    Auto-renew: <b>{person.auto_renew ? "On" : "Off"}</b>
                  </MDTypography>
                  <MDTypography variant="caption">
                    Promo:{" "}
                    <b>
                      {person.promo
                        ? `${person.promo.code}${
                            person.promo.applies_to_next_renewal ? " (applies next renewal)" : ""
                          }`
                        : "None"}
                    </b>
                  </MDTypography>
                  <MDTypography variant="caption">
                    Projected next year: <b>{money(person.projected_next_year_amount)}</b>
                  </MDTypography>
                </MDBox>
              </MDBox>
            ))}
          </MDBox>
        )}

        {!loading && !loadError && overview && step === "edit" && (
          <MDBox>
            <MDTypography variant="body2" color="text.secondary" mb={2}>
              Toggle edit on a person to change their payment status, subscription type,
              auto-renew, or promo code. Leave a person untouched to make no changes for them.
            </MDTypography>
            {overview.people.map((person) => {
              const isEditing = !!editingIds[person.id];
              const personEdits = edits[person.id] || {};
              return (
                <MDBox
                  key={person.id}
                  sx={{
                    border: `1px solid ${palette.neutral[200]}`,
                    borderRadius: "12px",
                    p: 2,
                    mb: 1.5,
                  }}
                >
                  <MDBox display="flex" justifyContent="space-between" alignItems="center">
                    <MDTypography variant="body1" fontWeight="bold">
                      {person.name || person.email}
                    </MDTypography>
                    <Icon
                      sx={{ cursor: "pointer", color: palette.brand[500] }}
                      onClick={() => toggleEditing(person.id)}
                    >
                      {isEditing ? "expand_less" : "edit"}
                    </Icon>
                  </MDBox>

                  {isEditing && (
                    <MDBox display="flex" gap={2} mt={2} flexWrap="wrap">
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <MDTypography variant="caption" mb={0.5}>
                          Payment status
                        </MDTypography>
                        <Select
                          size="small"
                          value={personEdits.payment_status ?? person.payment_status ?? ""}
                          onChange={(e) =>
                            setFieldEdit(person.id, "payment_status", e.target.value)
                          }
                        >
                          {PAYMENT_STATUSES.map((s) => (
                            <MenuItem key={s} value={s}>
                              {s}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <MDTypography variant="caption" mb={0.5}>
                          Subscription type
                        </MDTypography>
                        <Select
                          size="small"
                          value={personEdits.subscription_type ?? person.subscription_type ?? ""}
                          onChange={(e) =>
                            setFieldEdit(person.id, "subscription_type", e.target.value)
                          }
                        >
                          {SUBSCRIPTION_TYPES.map((s) => (
                            <MenuItem key={s} value={s}>
                              {s}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <MDBox display="flex" alignItems="center">
                        <MDTypography variant="caption" mr={1}>
                          Auto-renew
                        </MDTypography>
                        <Switch
                          checked={personEdits.auto_renew ?? person.auto_renew ?? false}
                          onChange={(e) => setFieldEdit(person.id, "auto_renew", e.target.checked)}
                        />
                      </MDBox>

                      <TextField
                        size="small"
                        label="Referral code"
                        defaultValue={person.promo?.code || ""}
                        onChange={(e) =>
                          setFieldEdit(person.id, "referral_code", e.target.value.trim())
                        }
                        sx={{ minWidth: 200 }}
                      />
                    </MDBox>
                  )}
                </MDBox>
              );
            })}
          </MDBox>
        )}

        {!loading && !loadError && overview && step === "confirm" && (
          <MDBox>
            <MDTypography variant="body2" fontWeight="bold" mb={1}>
              No charge, refund, or cancellation will be made. This only updates the stored
              payment status.
            </MDTypography>
            {changedEntries.length === 0 && (
              <MDTypography variant="body2" color="text.secondary">
                No changes were made.
              </MDTypography>
            )}
            {changedEntries.map(({ id, fields }) => {
              const person = personById(id);
              return (
                <MDBox
                  key={id}
                  sx={{
                    border: `1px solid ${palette.neutral[200]}`,
                    borderRadius: "12px",
                    p: 2,
                    mb: 1.5,
                  }}
                >
                  <MDTypography variant="body1" fontWeight="bold" mb={1}>
                    {person?.name || person?.email || id}
                  </MDTypography>
                  {Object.entries(fields).map(([field, newValue]) => (
                    <MDTypography variant="body2" key={field} sx={{ display: "block" }}>
                      {field}: <b>{String(person?.[field])}</b> {"→"} <b>{String(newValue)}</b>
                    </MDTypography>
                  ))}
                </MDBox>
              );
            })}
            {submitError && (
              <MDTypography variant="body2" color="error" mt={1}>
                {submitError}
              </MDTypography>
            )}
          </MDBox>
        )}

        {step === "done" && (
          <MDTypography variant="body2" color="success">
            Payment status updated successfully.
          </MDTypography>
        )}
      </DialogContent>

      <DialogActions>
        {step === "review" && overview && (
          <Button
            onClick={() => setStep("edit")}
            style={{ color: "white", background: palette.brand[500] }}
          >
            Edit
          </Button>
        )}
        {step === "edit" && (
          <>
            <Button
              onClick={() => setStep("review")}
              style={{ color: "white", background: palette.neutral[400] }}
            >
              Back
            </Button>
            <Button
              onClick={() => setStep("confirm")}
              style={{ color: "white", background: palette.brand[500] }}
            >
              Review Changes
            </Button>
          </>
        )}
        {step === "confirm" && (
          <>
            <Button
              onClick={() => setStep("edit")}
              style={{ color: "white", background: palette.neutral[400] }}
            >
              Back
            </Button>
            <Button
              disabled={changedEntries.length === 0 || submitting}
              onClick={submit}
              style={{ color: "white", background: palette.brand[500] }}
            >
              Confirm & Save
            </Button>
          </>
        )}
        <Button
          onClick={ClosePaymentWizardDialogHandler}
          style={{ color: "white", background: palette.pink[400] }}
        >
          {step === "done" ? "Close" : "Cancel"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentWizardDialog;
